import {World,Body,Vec3,Quaternion,Sphere,Cylinder,Material,ContactMaterial} from './vendor/cannon/cannon-es.js';

// Fixed substeps and interpolated poses keep 60/90/120 Hz displays independent of physics timing.
export function createFlaskPhysics(poses,{ambient=0}={}){
 const world=new World({gravity:new Vec3(0,0,0),allowSleep:false});
 world.solver.iterations=10;
 const glass=new Material('flask');
 world.addContactMaterial(new ContactMaterial(glass,glass,{friction:0.08,restitution:0.58,contactEquationStiffness:1e5,contactEquationRelaxation:6}));
 let collisions=0,pointerHits=0,time=0,accumulator=0,pointer=null,pointerWeight=0,settled=false;
 const fixed=1/120;
 const bodies=poses.map(p=>{
  const q=new Quaternion(...p.quaternion),offset=q.vmult(new Vec3(0,0.65*p.scale,0));
  const body=new Body({mass:1,material:glass,position:new Vec3(...p.position).vadd(offset),quaternion:q,linearDamping:0.5,angularDamping:0.72});
  body.addShape(new Sphere(0.61*p.scale),new Vec3(0,-0.05*p.scale,0));
  body.addShape(new Cylinder(0.22*p.scale,0.22*p.scale,0.7*p.scale,10),new Vec3(0,0.70*p.scale,0));
  body.addEventListener('collide',()=>collisions++);world.addBody(body);
  return {body,anchor:body.position.clone(),rest:q.clone(),scale:p.scale};
 });
 const v=new Vec3(),difference=new Vec3(),inverse=new Quaternion(),error=new Quaternion();
 function advance(){
  time+=fixed;
  pointerWeight+=((pointer?.active?1:0)-pointerWeight)*(1-Math.exp(-18*fixed));
  if(pointer)pointer.point.lerp(pointer.target,1-Math.exp(-22*fixed),pointer.point);
  for(const [index,{body,anchor,rest}] of bodies.entries()){
   anchor.vsub(body.position,v);
   body.force.x+=v.x*3.4-body.velocity.x;body.force.y+=v.y*3.4-body.velocity.y;body.force.z+=v.z*4.5-body.velocity.z*1.2;
   // A low, bounded current; each flask floats on its own phase, still colliding normally.
   if(settled&&ambient){const phase=index*2.399963;
    body.force.x+=Math.sin(time*.68+phase)*ambient;
    body.force.y+=Math.cos(time*.83+phase)*ambient*.8;
    body.torque.z+=Math.sin(time*.54+phase)*ambient*.18;
   }
   body.quaternion.conjugate(inverse);rest.mult(inverse,error);const sign=error.w<0?-1:1;
   body.torque.x+=error.x*sign*2;body.torque.y+=error.y*sign*2;body.torque.z+=error.z*sign*2;
   if(pointer&&pointerWeight>0.001){
    body.position.vsub(pointer.point,difference);
    const depth=difference.dot(pointer.normal);
    difference.x-=depth*pointer.normal.x;difference.y-=depth*pointer.normal.y;difference.z-=depth*pointer.normal.z;
    const distance=difference.length(),radius=1.9;
    if(distance<radius){
     if(distance<0.03)difference.set(0.3,0.8,0);difference.normalize();
     const strength=32*Math.pow(1-distance/radius,2)*pointerWeight;
     body.force.x+=difference.x*strength;body.force.y+=difference.y*strength;body.force.z+=difference.z*strength;
     body.torque.x+=difference.y*strength*0.045;body.torque.z-=difference.x*strength*0.08;pointerHits++;
    }
   }
   const speed=body.velocity.length(),spin=body.angularVelocity.length();
   if(speed>5)body.velocity.scale(5/speed,body.velocity);
   if(spin>3)body.angularVelocity.scale(3/spin,body.angularVelocity);
  }
  world.step(fixed);
 }
 function step(dt){
  accumulator+=Math.min(0.05,Math.max(0,dt));
  while(accumulator+1e-9>=fixed){advance();accumulator=Math.max(0,accumulator-fixed);}
  const alpha=accumulator/fixed;
  for(const {body} of bodies){
   body.previousPosition.lerp(body.position,alpha,body.interpolatedPosition);
   body.previousQuaternion.slerp(body.quaternion,alpha,body.interpolatedQuaternion);
  }
 }
 function settle(restPose=null){
  const valid=Array.isArray(restPose)&&restPose.length===bodies.length&&restPose.every(p=>p.position?.length===3&&p.quaternion?.length===4&&[...p.position,...p.quaternion].every(Number.isFinite));
  if(valid){bodies.forEach(({body},i)=>{body.position.set(...restPose[i].position);body.quaternion.set(...restPose[i].quaternion);body.aabbNeedsUpdate=true;});time=240*fixed;}
  else for(let i=0;i<240;i++)advance();
  // Rest at a collision-free pose instead of continually pulling overlapping source poses together.
  for(const {body,anchor,rest} of bodies){
   anchor.copy(body.position);rest.copy(body.quaternion);body.velocity.setZero();body.angularVelocity.setZero();
   body.previousPosition.copy(body.position);body.interpolatedPosition.copy(body.position);
   body.previousQuaternion.copy(body.quaternion);body.interpolatedQuaternion.copy(body.quaternion);
  }
  accumulator=0;settled=true;
 }
 return {bodies,step,settle,setPointer(point,normal){
  if(!point){if(pointer)pointer.active=false;return;}
  if(!pointer)pointer={point:new Vec3(...point),target:new Vec3(...point),normal:new Vec3(...normal),active:true};
  else{pointer.target.set(...point);pointer.normal.set(...normal);pointer.active=true;}
 },stats(){return {time,collisions,pointerHits,positions:bodies.map(({body})=>body.position.toArray()),maxDisplacement:Math.max(...bodies.map(({body,anchor})=>body.position.distanceTo(anchor)))};}};
}
